import LegalLayout, { Section, Note } from "./LegalLayout";
import { BRAND } from "@/data/content";
import { AlertTriangle } from "lucide-react";
import SEO from "@/components/SEO";

const UPDATED = "December 2025";

export default function MedicalDisclaimer() {
  return (
    <LegalLayout
      eyebrow="Legal · 03"
      title="Medical Disclaimer"
      updated={UPDATED}
    >
      <SEO
        title="Medical Disclaimer"
        description="Important research-use-only limitations for NextGen Pro Peptides and health disclosures for all NEXTGEN coaching content."
        url="https://nextgenlifestyle365.com/medical-disclaimer"
      />
      {/* Prominent research-use banner */}
      <div className="border border-yellow-500/50 bg-yellow-500/5 p-5 md:p-6 mt-6 flex flex-col md:flex-row gap-4 items-start md:items-center">
        <AlertTriangle size={32} strokeWidth={1.5} className="text-yellow-400 flex-shrink-0" />
        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs md:text-sm">
          <span className="font-display font-bold uppercase tracking-widest text-yellow-400">
            For Research Use Only
          </span>
          <span className="text-white/80 uppercase tracking-wider font-display">
            Not For Human Or Animal Consumption
          </span>
          <span className="text-white/80 uppercase tracking-wider font-display">
            Not Intended For Medical Or Therapeutic Use
          </span>
        </div>
      </div>

      <p data-testid="medical-intro" className="mt-8 text-white/75 font-light leading-relaxed">
        The information provided by {BRAND.name}, NextGen Pro Peptides, and
        Coach J on this website, in newsletters, in coaching materials, and in
        any related communications is offered for general informational and
        educational purposes only. It is <strong className="text-white">not</strong>
        intended to be, and should not be treated as, medical advice,
        diagnosis, or treatment.
      </p>

      <Section title="1. Not a Substitute for Professional Medical Care">
        <p>Always seek the advice of your physician or another qualified
        healthcare provider with any questions you may have regarding a
        medical condition, exercise program, diet change, supplement, or
        peptide-related decision. Never disregard professional medical advice
        or delay seeking it because of something you have read on this site
        or heard from Coach J.</p>
        <p>If you think you have a medical emergency, call your doctor or
        emergency services immediately.</p>
      </Section>

      <Section title="2. Coach J is Not a Medical Professional">
        <p>Coach J is a certified personal trainer, nutrition coach, and
        bodybuilding specialist (ISSA). Coach J is <strong className="text-white">
        not</strong> a licensed physician, registered dietitian, endocrinologist,
        pharmacist, or any other medical professional. Nothing offered by
        {BRAND.name} constitutes the practice of medicine.</p>
      </Section>

      <Section title="3. Peptides — Strict Research-Use Position">
        <p>All peptide compounds referenced or made available for inquiry
        through NextGen Pro Peptides are sold and discussed <strong className="text-yellow-400">
        strictly for laboratory research use only</strong>. They are:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Not for human consumption.</li>
          <li>Not for animal consumption.</li>
          <li>Not intended to diagnose, treat, cure, mitigate, or prevent any disease or condition.</li>
          <li>Not FDA-approved for therapeutic use in humans.</li>
          <li>Not to be resold to end-users for any non-research purpose.</li>
        </ul>
        <p>By submitting a peptide inquiry, you represent and warrant that you
        are a qualified researcher, that your inquiry is for legitimate
        laboratory research purposes, and that you will comply with all
        federal, state, and local laws applicable to your research.</p>
      </Section>

      <Section title="4. Individual Results Vary">
        <p>Any transformation results, testimonials, before/after images,
        performance metrics, or bloodwork changes shared on this site
        represent the individual experiences of specific clients. They are
        not typical, not guaranteed, and should not be interpreted as a
        promise of results for you. Your results will depend on many factors
        including genetics, effort, adherence, starting point, sleep,
        stress, medications, and pre-existing conditions.</p>
      </Section>

      <Section title="5. Pre-Existing Conditions & Contraindications">
        <p>Do <strong className="text-white">not</strong> begin any exercise,
        nutrition, supplement, or peptide-related program if you:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Are pregnant, breastfeeding, or trying to become pregnant.</li>
          <li>Have a diagnosed heart, lung, kidney, liver, thyroid, or metabolic condition.</li>
          <li>Have a history of cancer or an active malignancy.</li>
          <li>Take prescription medication that may interact with training, supplements, or peptides.</li>
          <li>Are under the care of a physician for any chronic condition.</li>
        </ul>
        <p>&mdash; without first obtaining written clearance from your treating physician.</p>
      </Section>

      <Section title="6. Informed Consent">
        <p>Engaging with {BRAND.name} for coaching or submitting a peptide
        inquiry constitutes your informed acknowledgement that you have
        read and understood this Medical Disclaimer, that you accept full
        personal responsibility for your health and safety, and that you
        release {BRAND.name} and Coach J from any and all claims arising
        from your use of the information provided.</p>
      </Section>

      <Section title="7. No FDA Evaluation">
        <p>Statements on this website regarding peptide research compounds,
        supplements, or nutrition strategies have not been evaluated by the
        U.S. Food and Drug Administration (FDA). Products and information
        provided are not intended to diagnose, treat, cure, or prevent any
        disease.</p>
      </Section>

      <Section title="8. Third-Party Content & Links">
        <p>Our site may contain links to third-party studies, articles, or
        websites. These links are for convenience only. We do not endorse
        or take responsibility for the content, accuracy, or practices of
        third-party sources.</p>
      </Section>

      <Section title="9. Contact">
        <p>Questions about this disclaimer?</p>
        <p>
          {BRAND.name}<br/>
          {BRAND.city} &middot; {BRAND.region}<br/>
          <a href={`mailto:${BRAND.email}`} className="text-ng-blue hover:underline">{BRAND.email}</a><br/>
          <a href={`tel:${BRAND.phone_link}`} className="text-ng-blue hover:underline">{BRAND.phone}</a>
        </p>
      </Section>

      <Note tone="warning">
        This Medical Disclaimer is a general template. Because peptide research
        compounds sit in a legally sensitive area, we <strong className="text-yellow-400">
        strongly recommend</strong> having a licensed attorney familiar with
        FDA, DSHEA, and applicable state laws review and customize this
        disclaimer for your specific business practices.
      </Note>
    </LegalLayout>
  );
}
